import { FormattedMessage } from 'react-intl';
import styled, {
  css,
  FlattenSimpleInterpolation,
} from 'styled-components/macro';
import tw from 'tailwind.macro';
import { ETransportType } from '../../../common/types';
import { roundShadowBoxStyle } from '../../../components/RoundShadowBox';
import {
  formatDuration,
  formatRoundPrice,
  getRoundPrice,
} from '../../../helpers/results';
import { CompaniesData } from '../../../services/data/types';
import {
  NOWRAP_TABLE_DATA,
  specificTableDataPropTypes,
  specificTableHeadersPropTypes,
  TRANSPORT_MEANS_PLURAL,
} from '../constants';
import messages from './messages';

const Container = styled.div`
  ${roundShadowBoxStyle}
  ${tw`overflow-x-scroll mb-abs4 mt-abs6`};
`;

const Table = styled.table`
  ${tw`w-full`};
  border-collapse: collapse;
`;

const Caption = styled.caption`
  ${tw`hidden`};
`;

const TR = styled.tr`
  height: 80px;

  th:nth-child(odd) {
    ${tw`bg-white`}
  }
`;

const TBody = styled.tbody`
  tr {
    td:nth-child(odd) {
      ${tw`bg-white`}
    }
  }

  tr:last-child {
    border-bottom: none;
  }
`;

const TH = styled.th<{ customStyle?: FlattenSimpleInterpolation }>`
  ${tw`px-abs4 whitespace-no-wrap bg-grey100`};

  ${({ customStyle }) => customStyle}
`;

const TD = styled.td<{
  customStyle?: FlattenSimpleInterpolation;
  nowrap?: boolean;
}>`
  ${tw`text-center bg-grey100`};

  padding: 1.5rem;
  white-space: ${(props) => (props.nowrap ? 'nowrap' : 'normal')};
  ${({ customStyle }) => customStyle}
`;

const customStyle = css`
  ${tw`p-abs1 px-abs1 md:px-abs4 bg-primary400 text-grey200`}
`;

export const TableSpecific = ({ data, headers }) => (
  <Container>
    <Table>
      <Caption>
        <FormattedMessage {...messages.detailedItineraryCaption} />
      </Caption>
      <thead>
        <TR>
          <TD customStyle={customStyle} />
          {headers.map((header) => (
            <TH key={`th${header.id}`} scope="col">
              {header.value}
            </TH>
          ))}
        </TR>
      </thead>
      <TBody>
        {data.map((row) => (
          <tr key={`tr${row.id}`}>
            <TH customStyle={customStyle} scope="row">
              <FormattedMessage {...messages[row.id]} />
            </TH>
            {headers.map((header) => {
              return (
                <TD
                  key={`td${row.id}${header.id}`}
                  nowrap={NOWRAP_TABLE_DATA.includes(row.id)}
                >
                  <strong>{row.value[header.id]}</strong>
                </TD>
              );
            })}
          </tr>
        ))}
      </TBody>
    </Table>
  </Container>
);

const tableThCustomStyle = css`
  ${customStyle}

  ${tw`md:sticky`}
  left:0;
`;

TableSpecific.propTypes = {
  data: specificTableDataPropTypes,
  headers: specificTableHeadersPropTypes,
};

type TableRowProps = {
  label: React.ReactNode;
  values: (company: string) => React.ReactNode;
  availableCompanies: string[];
};

const TableRow = ({ availableCompanies, label, values }: TableRowProps) => (
  <tr>
    <TH customStyle={tableThCustomStyle} scope="row">
      {label}
    </TH>
    {availableCompanies.map((company, index) => (
      <TD key={index}>
        <strong>{values(company)}</strong>
      </TD>
    ))}
  </tr>
);

type TableHeaderProps = {
  availableCompanies: string[];
};

const TableHeader = ({ availableCompanies }: TableHeaderProps) => (
  <thead>
    <TR>
      <TD customStyle={tableThCustomStyle} />
      {availableCompanies.map((companyName) => (
        <TH key={`th-${companyName}`} scope="col">
          {companyName}
        </TH>
      ))}
    </TR>
  </thead>
);

type CompaniesTableProps = {
  companiesData: CompaniesData | null;
  transportType?: ETransportType;
};

export const CompaniesTable = ({
  companiesData,
  transportType,
}: CompaniesTableProps) => {
  const availableCompanies = companiesData ? Object.keys(companiesData) : [];

  if (availableCompanies.length === 0 || !transportType) {
    return null;
  }

  return (
    <Container>
      <Table>
        <TableHeader availableCompanies={availableCompanies} />
        <TBody>
          <TableRow
            availableCompanies={availableCompanies}
            label={
              <FormattedMessage
                {...messages.dailyTransportType}
                values={{
                  transportType: (
                    <span style={{ textTransform: 'capitalize' }}>
                      <FormattedMessage
                        {...messages[TRANSPORT_MEANS_PLURAL[transportType]]}
                      />
                    </span>
                  ),
                }}
              />
            }
            values={(company) => (
              <FormattedMessage
                {...messages.dailyTransportTypeText}
                values={{
                  numberOfTripsPerDay: companiesData[company].dailyTrips,
                  transportType: (
                    <FormattedMessage
                      {...messages[
                        companiesData[company].dailyTrips > 1
                          ? TRANSPORT_MEANS_PLURAL[transportType]
                          : transportType
                      ]}
                    />
                  ),
                  company,
                }}
              />
            )}
          />
          <TableRow
            availableCompanies={availableCompanies}
            label={<FormattedMessage {...messages.firstLastDeparture} />}
            values={(company) => (
              <FormattedMessage
                {...messages.firstLastDepartureText}
                values={{
                  transportType: (
                    <FormattedMessage {...messages[transportType]} />
                  ),
                  firstTripDepartureTime:
                    companiesData[company].firstTrip.departureTime,
                  lastTripDepartureTime:
                    companiesData[company].lastTrip.departureTime,
                }}
              />
            )}
          />
          <TableRow
            availableCompanies={availableCompanies}
            label={<FormattedMessage {...messages.earliestLatestArrival} />}
            values={(company) => (
              <FormattedMessage
                {...messages.earliestLatestArrivalText}
                values={{
                  firstTripArrivalTime:
                    companiesData[company].firstTrip.arrivalTime,
                  lastTripArrivalTime:
                    companiesData[company].lastTrip.arrivalTime,
                }}
              />
            )}
          />
          <TableRow
            availableCompanies={availableCompanies}
            label={<FormattedMessage {...messages.priceRangeMinMaxAvg} />}
            values={(company) =>
              companiesData[company].priceCents.min ===
              companiesData[company].priceCents.max ? (
                formatRoundPrice(
                  getRoundPrice(companiesData[company].priceCents.min),
                )
              ) : (
                <FormattedMessage
                  {...messages.priceRangeMinMaxAvgText}
                  values={{
                    minPrice: formatRoundPrice(
                      getRoundPrice(companiesData[company].priceCents.min),
                    ),
                    maxPrice: formatRoundPrice(
                      getRoundPrice(companiesData[company].priceCents.max),
                    ),
                    meanPrice: formatRoundPrice(
                      getRoundPrice(companiesData[company].priceCents.mean),
                    ),
                  }}
                />
              )
            }
          />
          <TableRow
            availableCompanies={availableCompanies}
            label={<FormattedMessage {...messages.durationMinMaxAvg} />}
            values={(company) =>
              companiesData[company].durationMinutes.min ===
              companiesData[company].durationMinutes.max ? (
                formatDuration(companiesData[company].durationMinutes.min)
              ) : (
                <FormattedMessage
                  {...messages.durationMinMaxAvgText}
                  values={{
                    minDuration: formatDuration(
                      companiesData[company].durationMinutes.min,
                    ),
                    maxDuration: formatDuration(
                      companiesData[company].durationMinutes.max,
                    ),
                    meanDuration: formatDuration(
                      companiesData[company].durationMinutes.mean,
                    ),
                  }}
                />
              )
            }
          />
          <TableRow
            availableCompanies={availableCompanies}
            label={<FormattedMessage {...messages.possibleDepartureStations} />}
            values={(company) =>
              companiesData[company].stations.origins
                .map(({ name }) => name)
                .join(', ')
            }
          />
          <TableRow
            availableCompanies={availableCompanies}
            label={<FormattedMessage {...messages.possibleArrivalStations} />}
            values={(company) =>
              companiesData[company].stations.destinations
                .map(({ name }) => name)
                .join(', ')
            }
          />
        </TBody>
      </Table>
    </Container>
  );
};
